import { ConveyLoading } from "./ConveyLoading";
import { ConveyTranscribe } from "./ConveyTranscribe";
import { ConveySocket } from "./ConveySocket";
import { getStore, getStoreState } from "./reduxHelpers";
import { setLoading, setNoLoading, showContainer } from "@actions";
import type { IConveyTranscribeCallProps, ISDKMeta } from "./types";

/**
 * One live transcribe surface.
 *
 * The socket, redux and React layers underneath are all keyed per call - SIP
 * rooms are `sip:{agencyId}:{correlationId}`, the conversations slice is keyed by
 * conversation id, and every ConveyTranscribe owns its own React root - so a host
 * can run several at once. This class holds the per-call state that used to live
 * on the SDK facade (container, correlationId, callbacks, live-audio target) so
 * those surfaces stop overwriting each other.
 *
 * It deliberately exposes the same chainable `onX` setters the SDK does, so
 * `transcribeCall(params).onReady(...).onLiveAudio(...)` keeps working and simply
 * binds to this call instead of to the whole SDK.
 */
export class TranscribeSession {
  readonly conversationId: string;
  readonly correlationId: string;
  readonly $container: string;
  agencyId: string;
  params: IConveyTranscribeCallProps;

  private loading: ConveyLoading | null = null;
  private transcribe: ConveyTranscribe | null = null;
  private started = false;
  private destroyed = false;

  private readyCallback?: (meta: ISDKMeta) => void;
  private transcribeCallback?: (eventData: Record<any, any>) => void;
  private uiReadyCallback?: () => void;
  private liveAudioCallback?: (eventData: Record<any, any>, dualChannel: boolean, live: boolean) => void;
  private keywordCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  private sentimentScoreCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  private summaryCallback?: (eventData: string, isNew: boolean) => void;
  private errorCallback?: (error: Error) => void;

  constructor(params: IConveyTranscribeCallProps & { agencyId?: string }) {
    this.params = params;
    this.conversationId = params.conversationId;
    this.correlationId = params.correlationId;
    this.$container = params.$container;
    this.agencyId = params.agencyId || params.conversation?.agency_id;
  }

  /** Identifies the session in the SDK's registry - see SDK.transcribeSessionKey. */
  get key() {
    return this.$container || this.conversationId || this.correlationId;
  }

  get isDestroyed() {
    return this.destroyed;
  }

  get isStarted() {
    return this.started;
  }

  /** The rendered surface, for hosts that reach for it (kept for parity with `SDK.transcribe`). */
  get surface() {
    return this.transcribe;
  }

  onReady(callback: (meta: ISDKMeta) => void) {
    this.readyCallback = callback;
    return this;
  }

  onTranscribe(callback: (eventData: Record<any, any>) => void) {
    this.transcribeCallback = callback;
    return this;
  }

  onTranscribeUIReady(callback: () => void) {
    this.uiReadyCallback = callback;
    return this;
  }

  onLiveAudio(callback: (eventData: Record<any, any>, dualChannel: boolean, live: boolean) => void) {
    this.liveAudioCallback = callback;
    return this;
  }

  onKeyword(callback: (eventData: Record<any, any>, isNew: boolean) => void) {
    this.keywordCallback = callback;
    return this;
  }

  onSentimentScore(callback: (eventData: Record<any, any>, isNew: boolean) => void) {
    this.sentimentScoreCallback = callback;
    return this;
  }

  onSummary(callback: (eventData: string, isNew: boolean) => void) {
    this.summaryCallback = callback;
    return this;
  }

  onError(callback: (error: Error) => void) {
    this.errorCallback = callback;
    return this;
  }

  notifyReady(meta: ISDKMeta) {
    this.readyCallback?.(meta);
  }

  notifyError(error: Error) {
    this.errorCallback?.(error);
  }

  /**
   * Mounts the surface into its own container. Safe to call again for the same
   * call - the container is reused and only re-rendered.
   */
  start() {
    if (this.destroyed) return this;
    if (!document.getElementById(this.$container)) return this;

    this.teardownSurface();

    getStore().dispatch(setLoading());
    this.loading = new ConveyLoading({
      $container: this.$container,
      root: null,
      experience: "transcribe",
      // The container id already carries the conversation, so the surfaces stay
      // distinct even when two calls share a correlation id prefix.
      phoneNumber: this.correlationId
    });
    getStore().dispatch(setNoLoading());

    this.transcribe = new ConveyTranscribe({
      correlationId: this.correlationId,
      root: this.loading ? this.loading.getRoot() : null,
      $container: this.$container,
      $summaryContainer: this.params.$summaryContainer,
      $sentimentContainer: this.params.$sentimentContainer,
      $keywordsContainer: this.params.$keywordsContainer,
      talkback: this.params.talkback,
      callerLanguage: this.params.callerLanguage,
      neuralName: this.params.neuralName,
      showUploader: this.params.showUploader,
      conversationId: this.conversationId,
      conversationAgencyId: this.agencyId,
      recipientAudio: this.params.recipientAudio,
      dualChannel: this.params.dualChannel,
      agentAudio: this.params.agentAudio,
      numberCapabilities: this.params.numberCapabilities,
      interpretationView: this.params.interpretationView,
      streaming: this.params.streaming,
      callback: (...args) => this.transcribeCallback?.(...args),
      onUIReadyCallback: (...args) => this.uiReadyCallback?.(...args),
      onKeywordCallback: (...args) => this.keywordCallback?.(...args),
      onSentimentScoreCallback: (...args) => this.sentimentScoreCallback?.(...args),
      onSummaryCallback: (...args) => this.summaryCallback?.(...args),
      onLiveAudioCallback: (...args) => this.liveAudioCallback?.(...args)
    });

    this.started = true;
    getStore().dispatch(showContainer(this.correlationId, "transcribe"));
    return this;
  }

  /**
   * Live call audio is published to a SocketIO room per call, so each session
   * joins and leaves its own room - one pinned call can be listened to without
   * pulling audio for the others across the wire.
   */
  startLiveAudio() {
    ConveySocket.joinSipAudio(this.liveAudioTarget());
    return this;
  }

  stopLiveAudio() {
    ConveySocket.leaveSipAudio(this.liveAudioTarget());
    return this;
  }

  isLiveAudioStarted() {
    return ConveySocket.isSipAudioJoined(this.liveAudioTarget());
  }

  destroy() {
    this.destroyed = true;
    this.started = false;
    this.stopLiveAudio();
    this.teardownSurface();
  }

  private teardownSurface() {
    if (this.transcribe) {
      this.transcribe.destroy && this.transcribe.destroy();
      this.transcribe = null;
    }
    if (this.loading) {
      this.loading.destroy && this.loading.destroy();
      this.loading = null;
    }
  }

  private liveAudioTarget() {
    // Mirrors how the transcribe surface resolves the publishing tenant: a
    // live-shared call is published under the owning agency, not the viewer's.
    const conversation = getStoreState().conversations?.[this.conversationId]?.entity;

    return {
      agencyId: conversation?.agency_id || this.agencyId,
      correlationId: this.correlationId
    };
  }
}
